import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowClockwise, Plus, Tag, X } from "@phosphor-icons/react";
import type { CodeCategoryNode } from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, EmptyState, Field, LoadError, Modal, StatusBadge } from "./components";
import { beijingInputToIso, beijingInputValue } from "./promotion-time";
import { ProductSearch } from "./ProductSearch";
import { useProductSearch } from "./useProductSearch";
import { productSearchPath } from "./catalog-selection";
import { ProductCategoryFilter } from "./ProductCategoryFilter";
import { useSynchronizedCodeCategories } from "./code-category-sync";
import "./promotions.css";

type PromotionStatus = "SCHEDULED" | "ACTIVE" | "ENDED" | "DISABLED";
type Promotion = {
  id: string;
  name: string;
  scope: "ALL" | "PRODUCT";
  productId: string | null;
  productName: string | null;
  discountPercent: number;
  startsAt: string;
  endsAt: string;
  active: boolean;
  status: PromotionStatus;
  createdAt: string;
};

export type PromotionProduct = { id: string; name: string; code: string };
type Notify = (message: string, tone?: "success" | "error" | "info") => void;

const statusLabels: Record<PromotionStatus, string> = {
  ACTIVE: "进行中", SCHEDULED: "未开始", ENDED: "已结束", DISABLED: "已停用",
};
const statusTones = {
  ACTIVE: "success", SCHEDULED: "info", ENDED: "neutral", DISABLED: "warning",
} as const;

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function beijingDisplay(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(value));
}

export function PromotionsAdmin({ isAdmin, notify }: { isAdmin: boolean; notify: Notify }) {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<PromotionStatus | "ALL">("ALL");
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  const [disabling, setDisabling] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void request<{ promotions: Promotion[] }>("/api/v1/admin/promotions")
      .then((result) => { if (!cancelled) setPromotions(result.promotions); })
      .catch((caught: unknown) => { if (!cancelled) setError(errorMessage(caught, "促销活动加载失败")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);

  function refresh() {
    setLoading(true);
    setError("");
    setReload((value) => value + 1);
  }

  async function disable(promotion: Promotion) {
    setDisabling(promotion.id);
    setActionError("");
    try {
      await request(`/api/v1/admin/promotions/${promotion.id}/disable`, { method: "POST" });
      setPromotions((current) => current.map((item) => item.id === promotion.id
        ? { ...item, active: false, status: "DISABLED" } : item));
      notify(`已停用「${promotion.name}」`);
      refresh();
    } catch (caught) {
      setActionError(errorMessage(caught, "停用失败，请重试"));
    } finally {
      setDisabling(null);
    }
  }

  const visible = promotions.filter((item) => filter === "ALL" || item.status === filter);
  return <>
    <section className="admin-intro promotions-intro">
      <div>
        <p className="eyebrow">限时优惠</p>
        <h2>设好折扣，到点自动生效</h2>
        <p>全场或指定商品均可设置。8 折表示按商品原价的 80% 销售，到期自动恢复。</p>
      </div>
      {isAdmin && <Button onClick={() => setCreating(true)}><Plus />新建促销</Button>}
    </section>
    <div className="promotions-policy">
      <Tag /><p>原价始终保留；同一商品遇到多个活动时取最优惠价格，不叠加折扣。已提交的订单价格保持下单时的金额。</p>
    </div>
    <section className="admin-toolbar promotions-toolbar">
      <div className="promotions-filters" role="group" aria-label="促销状态筛选">
        {(["ALL", "ACTIVE", "SCHEDULED", "ENDED", "DISABLED"] as const).map((status) =>
          <button key={status} type="button" aria-pressed={filter === status}
            className={filter === status ? "active" : ""} onClick={() => setFilter(status)}>
            {status === "ALL" ? "全部" : statusLabels[status]}
          </button>)}
      </div>
      <Button variant="secondary" onClick={refresh} disabled={loading}>
        <ArrowClockwise />刷新
      </Button>
      <span className="promotions-timezone">时间均为北京时间（UTC+8）</span>
    </section>
    {actionError && <div className="inline-error" role="alert">{actionError}</div>}
    {loading ? <div className="loading-block" role="status">正在加载促销活动…</div>
      : error ? <LoadError title="促销活动暂时无法加载" description={error} onRetry={refresh} />
        : visible.length === 0 ? <EmptyState title={filter === "ALL" ? "还没有促销活动" : "此状态下暂无活动"}
          description={filter === "ALL" ? "为全场或指定商品设置折扣和时间，客户将自动看到有效优惠。" : "切换其他状态查看活动记录。"} />
          : <section className="promotions-list" aria-label="促销活动列表">
            {visible.map((promotion) => <article key={promotion.id} className="promotion-card">
              <div className="promotion-card__discount"><strong>{promotion.discountPercent / 10}</strong><span>折</span></div>
              <div className="promotion-card__details">
                <div className="promotion-card__heading"><h3>{promotion.name}</h3>
                  <StatusBadge tone={statusTones[promotion.status]}>{statusLabels[promotion.status]}</StatusBadge></div>
                <p>{promotion.scope === "ALL" ? "全场商品" : promotion.productName ?? "指定商品"}
                  {promotion.scope === "PRODUCT" && <span className="promotion-scope-note">含该商品的所有规格</span>}</p>
                <p className="promotion-card__period"><time dateTime={promotion.startsAt}>{beijingDisplay(promotion.startsAt)}</time>
                  <span>至</span><time dateTime={promotion.endsAt}>{beijingDisplay(promotion.endsAt)}</time></p>
              </div>
              {isAdmin && promotion.active && (promotion.status === "ACTIVE" || promotion.status === "SCHEDULED") &&
                <Button variant="secondary" loading={disabling === promotion.id} disabled={disabling !== null}
                  aria-label={`停用 ${promotion.name}`} onClick={() => void disable(promotion)}>停用</Button>}
            </article>)}
          </section>}
    {!isAdmin && <div className="readonly-note">查看员可以查看促销记录，不能新增或停用活动。</div>}
    {creating && <PromotionEditor onClose={() => setCreating(false)} onSaved={() => {
      setCreating(false); notify("促销活动已保存"); refresh();
    }} />}
  </>;
}

export function PromotionEditor({ product, onClose, onSaved }: {
  product?: PromotionProduct;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [scope, setScope] = useState<"ALL" | "PRODUCT">(product ? "PRODUCT" : "ALL");
  const [selectedProduct, setSelectedProduct] = useState<PromotionProduct | null>(product ?? null);
  const [name, setName] = useState("");
  const [discount, setDiscount] = useState("8");
  const [startsAt, setStartsAt] = useState(() => beijingInputValue(new Date()));
  const [endsAt, setEndsAt] = useState(() => beijingInputValue(new Date(Date.now() + 7 * 86400_000)));
  const [results, setResults] = useState<PromotionProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [category, setCategory] = useState("");
  const [categoryData, setCategoryData] = useState<{ categories: Array<{ id: string; name: string; productCount?: number }>; codeCategories?: CodeCategoryNode[] }>({ categories: [] });
  const codeCategories = useSynchronizedCodeCategories(categoryData.codeCategories);
  const [categoryError, setCategoryError] = useState("");
  const search = useProductSearch({ onCommit: () => {
    setResults([]); setSearchError(""); setSearching(true);
  } });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const searchSequence = useRef(0);

  useEffect(() => {
    if (scope !== "PRODUCT" || selectedProduct) return;
    const controller = new AbortController();
    void request<typeof categoryData>("/api/v1/admin/categories", { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) { setCategoryData(result); setCategoryError(""); } })
      .catch((caught: unknown) => { if (!controller.signal.aborted) setCategoryError(errorMessage(caught, "分类暂时无法加载，请通过关键词搜索商品")); });
    return () => controller.abort();
  }, [scope, selectedProduct]);

  useEffect(() => {
    const sequence = ++searchSequence.current;
    if (scope !== "PRODUCT" || selectedProduct || (!search.query && !category)) return;
    const controller = new AbortController();
    void request<{ products: PromotionProduct[] }>(productSearchPath("/api/v1/admin/products", search.query, category), { signal: controller.signal })
      .then((result) => { if (sequence === searchSequence.current) setResults(result.products.slice(0, 20)); })
      .catch((caught: unknown) => { if (sequence === searchSequence.current) setSearchError(errorMessage(caught, "商品搜索失败，请重新搜索")); })
      .finally(() => { if (sequence === searchSequence.current) setSearching(false); });
    return () => { controller.abort(); searchSequence.current += 1; };
  }, [search.query, search.revision, category, scope, selectedProduct]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    setError("");
    const discountPercent = Math.round(Number(discount) * 10);
    if (!/^\d(?:\.\d)?$/.test(discount) || discountPercent < 1 || discountPercent > 99) {
      setError("请输入 0.1 到 9.9 折，最多保留一位小数。"); return;
    }
    if (scope === "PRODUCT" && !selectedProduct) { setError("请先搜索并选择商品。"); return; }
    const startIso = beijingInputToIso(startsAt);
    const endIso = beijingInputToIso(endsAt);
    if (!startIso || !endIso) { setError("请填写有效的北京时间。"); return; }
    if (Date.parse(endIso) <= Date.parse(startIso)) { setError("结束时间必须晚于开始时间。"); return; }
    if (Date.parse(endIso) <= Date.now()) { setError("结束时间必须晚于现在。"); return; }
    savingRef.current = true;
    setSaving(true);
    try {
      await request("/api/v1/admin/promotions", { method: "POST", body: JSON.stringify({
        name: name.trim() || `${scope === "ALL" ? "全场" : "单品"}${discountPercent / 10}折限时优惠`,
        scope, ...(scope === "PRODUCT" ? { productId: selectedProduct!.id } : {}),
        discountPercent, startsAt: startIso, endsAt: endIso,
      }) });
      onSaved();
    } catch (caught) { setError(errorMessage(caught, "保存失败，已保留填写内容，请重试")); }
    finally { savingRef.current = false; setSaving(false); }
  }

  return <Modal title={product ? "设置单品折扣" : "新建限时促销"}
    description="按商品原价计算折扣；活动到期或停用后，自动按其他有效活动或原价销售。"
    onClose={() => { if (!savingRef.current) onClose(); }}
    footer={<><Button variant="secondary" disabled={saving} onClick={onClose}>取消</Button>
      <Button type="submit" form="promotion-editor" loading={saving}>保存促销</Button></>}>
    <form id="promotion-editor" className="promotion-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <Field label="活动名称" hint="可不填，系统将按范围和折扣自动命名。"><input aria-label="活动名称" value={name} onChange={(event) => setName(event.target.value)}
        placeholder="例如：十月回馈活动" maxLength={100} disabled={saving} /></Field>
      <Field label="优惠范围"><select value={scope} disabled={saving} onChange={(event) => {
        setScope(event.target.value as "ALL" | "PRODUCT"); setResults([]); setSearchError(""); setSearching(Boolean(search.query || category));
      }}>
        <option value="ALL">全场商品</option><option value="PRODUCT">指定商品（含所有规格）</option>
      </select></Field>
      {scope === "PRODUCT" && <section className="promotion-product-picker" aria-label="选择促销商品">
        {selectedProduct ? <div className="promotion-selected-product"><span><b>{selectedProduct.name}</b><small>{selectedProduct.code}</small></span>
          <Button variant="ghost" aria-label="更换促销商品" disabled={saving} onClick={() => { setSelectedProduct(null); search.reset(""); setCategory(""); setResults([]); setSearchError(""); setSearching(false); }}><X />更换</Button></div>
          : <><ProductSearch search={search} ariaLabel="搜索促销商品" disabled={saving} />
          <div className="category-navigation"><ProductCategoryFilter nodes={codeCategories ?? []} legacyCategories={categoryData.categories ?? []}
            value={category} disabled={saving} onChange={(value) => {
              if (value === category) return;
              setCategory(value); setResults([]); setSearchError(""); setSearching(Boolean(search.query || value));
            }} /></div>
          {categoryError && <p className="field__hint">{categoryError}</p>}
          {search.pending || ((search.query || category) && searching) ? <p role="status">正在搜索商品…</p> : searchError ? <p className="field__error" role="alert">{searchError}</p>
            : (search.query || category) && results.length === 0 ? <p>没有匹配商品，请换个关键词。</p> : null}
          <div className="promotion-search-results">{!search.pending && results.map((item) => <button key={item.id} type="button" disabled={saving || searching}
            onClick={() => setSelectedProduct(item)}><b>{item.name}</b><small>{item.code}</small><Plus aria-hidden="true" /></button>)}</div>
          {results.length === 20 && <p className="field__hint">当前显示前 20 个匹配商品，可输入更完整的关键词查找。</p>}</>}
      </section>}
      <div className="promotion-discount-field">
        <Field label="折扣（折）" hint="0.1–9.9 折，支持一位小数。"><input aria-label="折扣（折）" type="number" min="0.1" max="9.9" step="0.1" required
          value={discount} disabled={saving} onChange={(event) => setDiscount(event.target.value)} /></Field>
        <Button type="button" variant="secondary" disabled={saving} onClick={() => setDiscount("8")}>一键设为 8 折</Button>
      </div>
      <div className="promotion-price-example">{Number(discount) >= 0.1 && Number(discount) <= 9.9
        ? `例如原价 ¥100，${discount} 折后的现价为 ¥${(Number(discount) * 10).toFixed(2)}。`
        : "折后价格 = 原价 × 折扣 ÷ 10。"} 原价不会被覆盖。</div>
      <div className="form-grid promotion-dates">
        <Field label="开始时间（北京时间）"><input type="datetime-local" required value={startsAt} disabled={saving}
          onChange={(event) => setStartsAt(event.target.value)} /></Field>
        <Field label="结束时间（北京时间）"><input type="datetime-local" required value={endsAt} disabled={saving}
          onChange={(event) => setEndsAt(event.target.value)} /></Field>
      </div>
      <p className="promotion-form-note">所有时间均按北京时间（UTC+8）。开始时生效，结束时失效；多个优惠取最低价，不叠加。</p>
    </form>
  </Modal>;
}
